import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Star, Compass } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PopularTour {
  id: string;
  slug: string;
  title: string;
  short_description: string | null;
  main_image_url: string | null;
  duration: string | null;
  adult_price: number | null;
  is_featured: boolean | null;
}

const PopularTours = () => {
  const [tours, setTours] = useState<PopularTour[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("tours")
        .select("id,slug,title,short_description,main_image_url,duration,adult_price,is_featured")
        .eq("is_active", true)
        .order("is_featured", { ascending: false })
        .order("sort_order", { ascending: true })
        .limit(6);
      setTours(data ?? []);
      setLoading(false);
    })();
  }, []);

  if (!loading && tours.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">
            Most popular
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">
            Our most loved tours & excursions.
          </h2>
        </div>
        <Link
          to="/tours"
          className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all shrink-0"
        >
          View all tours
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {tours.map((t) => (
          <Link
            key={t.id}
            to={`/tours/${t.slug}`}
            className="group relative overflow-hidden rounded-3xl bg-card shadow-card hover:shadow-elegant transition-smooth"
          >
            <div className="aspect-[4/3] overflow-hidden bg-muted">
              {t.main_image_url ? (
                <img
                  src={t.main_image_url}
                  alt={t.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <Compass className="w-10 h-10" />
                </div>
              )}
            </div>
            {t.is_featured && (
              <div className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold uppercase tracking-[0.15em]">
                <Star className="w-3 h-3 fill-current" />
                Featured
              </div>
            )}
            <div className="p-6">
              <h3 className="font-display text-xl font-semibold leading-snug group-hover:text-primary transition-smooth">
                {t.title}
              </h3>
              {t.short_description && (
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{t.short_description}</p>
              )}
              <div className="mt-4 flex items-center justify-between">
                {t.duration ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" />
                    {t.duration}
                  </span>
                ) : (
                  <span />
                )}
                {t.adult_price ? (
                  <span className="text-sm font-semibold">
                    <span className="text-muted-foreground font-normal text-xs">from </span>${t.adult_price}
                  </span>
                ) : null}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default PopularTours;
